import { useEffect, useRef, useState } from "react";
import { Liste, PfeilRechts, Sprechblase, Textmarker } from "./Symbole";
import Farbwahl from "./Markerfarben";

/*
  „Wohin damit?“ — die Frage direkt an der zitierten Textstelle.

  Warum hier und nicht unten am Rand: Wer eine Stelle im Text ausgewählt hat,
  schaut genau dorthin. Eine Leiste am unteren Bildschirmrand zwingt den Blick
  weg und den Daumen quer über das Tablet. Das Pop-up geht dort auf, wo eben
  noch der Finger war.

  Und warum überhaupt gefragt wird: Beim Lesen einer Vorlage entstehen vier
  verschiedene Dinge, die man heute alle in dieselbe WhatsApp-Gruppe wirft —
  eine Randbemerkung, ein Diskussionsbeitrag, ein Hinweis für einen anderen
  Tagesordnungspunkt und eine Aufgabe für sich selbst. Wenn die Software
  gleich fragt, landen sie an vier verschiedenen, richtigen Stellen.

  Die wichtigste dieser Unterscheidungen ist die zwischen Notiz und Chat.
  Sie ist in KOMMUNIKATION.md begründet und lautet kurz: Am Dokument wird
  angemerkt, nicht diskutiert.
*/

/* Abstand zum Bildschirmrand, damit nichts abgeschnitten wird. */
const RAND = 12;
const BREITE = 320;

export default function Zitatziel({
  zitat,
  stelle,
  tops,
  aktuellerTop,
  privat = false,
  eigen = false,
  bereitsZitiert = false,
  notizVorhanden = false,
  farbe = "gelb",
  aufWahl,
  aufFarbe,
  aufEntfernen,
  aufAbbrechen,
}) {
  const [andereOffen, setzeAndereOffen] = useState(false);
  const kastenRef = useRef(null);

  useEffect(() => {
    const beiTaste = (e) => {
      if (e.key === "Escape") aufAbbrechen();
    };
    window.addEventListener("keydown", beiTaste);
    return () => window.removeEventListener("keydown", beiTaste);
  }, [aufAbbrechen]);

  const lage = platziere(stelle);

  const andere = tops.filter((t) => t.id !== aktuellerTop.id && !t.formal);

  return (
    <>
      <button className="zziel__hinterfeld" aria-label="Abbrechen" onClick={aufAbbrechen} />

      <div
        ref={kastenRef}
        className="zziel"
        style={lage}
        role="dialog"
        aria-label="Wohin soll das Zitat?"
      >
        <p className="zziel__frage">Wohin damit?</p>
        <blockquote className="zziel__zitat">„{kuerze(zitat)}“</blockquote>

        {bereitsZitiert && (
          <p className="zziel__lage">
            Diese Stelle wird in der Fraktion bereits besprochen. Du kannst sie erneut
            weitergeben.
          </p>
        )}

        {/*
          Eine private Markierung weiterzugeben macht sie für alle sichtbar. Das
          muss vorher dastehen, nicht hinterher — sonst gibt jemand eine Notiz
          heraus, die er für sich behalten wollte.
        */}
        {privat && (
          <p className="zziel__lage">
            <strong>Bisher nur für dich sichtbar.</strong> Wenn du sie weitergibst, sieht die
            ganze Fraktion die Stelle samt Zitat.
          </p>
        )}

        {!andereOffen && aufFarbe && (
          <div className="zziel__farben">
            <span className="klein leise">Farbe</span>
            <Farbwahl gewaehlt={farbe} aufWahl={aufFarbe} beschriftung="Farbe der Markierung" />
          </div>
        )}

        {!andereOffen ? (
          <div className="zziel__wahl">
            {/*
              Notieren und reden sind zwei verschiedene Dinge, und die
              Entscheidung fällt hier — an der Textstelle, wo sie entsteht.

              Eine Notiz bleibt am Dokument stehen und erwartet keine
              Antwort. Ein Chatbeitrag geht an Menschen und erwartet eine. Wer
              beides in denselben Topf wirft, findet später keines von beidem
              wieder. Deshalb steht „Notiz schreiben“ oben: Beim Lesen
              einer Vorlage ist es der häufigere Fall.
            */}
            <Ziel
              symbol={Textmarker}
              titel={notizVorhanden ? "Notiz bearbeiten" : "Notiz schreiben"}
              zusatz="Bleibt am Rand der Vorlage stehen"
              onClick={() => aufWahl({ art: "notiz" })}
            />
            <Ziel
              symbol={Sprechblase}
              titel="In den Chat"
              zusatz={`Zur Besprechung in der Fraktion · TOP ${aktuellerTop.nummer}`}
              onClick={() => aufWahl({ art: "chat" })}
            />
            {andere.length > 0 && (
              <Ziel
                symbol={PfeilRechts}
                titel="In den Chat zu einem anderen Punkt"
                zusatz="Die Stelle gehört woanders hin"
                onClick={() => setzeAndereOffen(true)}
              />
            )}
            <Ziel
              symbol={Liste}
              titel="Als Aufgabe für mich"
              zusatz="Landet in deiner Aufgabenliste"
              onClick={() => aufWahl({ art: "aufgabe" })}
            />
            <Ziel
              symbol={Textmarker}
              titel="Nur markieren"
              zusatz="Bleibt im Dokument stehen"
              onClick={() => aufWahl({ art: "markierung" })}
            />
          </div>
        ) : (
          <div className="zziel__wahl">
            <p className="klein leise" style={{ marginBottom: "var(--luft-2)" }}>
              Zu welchem Punkt?
            </p>
            {andere.map((t) => (
              <Ziel
                key={t.id}
                symbol={Sprechblase}
                titel={`TOP ${t.nummer}`}
                zusatz={t.titel}
                onClick={() => aufWahl({ art: "chat", topId: t.id })}
              />
            ))}
            <button className="knopf knopf--schlicht" onClick={() => setzeAndereOffen(false)}>
              Zurück
            </button>
          </div>
        )}

        <div className="zziel__fuss">
          {eigen && aufEntfernen && !andereOffen && (
            <button className="knopf knopf--schlicht" onClick={aufEntfernen}>
              Markierung entfernen
            </button>
          )}
          <button className="knopf knopf--schlicht" onClick={aufAbbrechen}>
            Abbrechen
          </button>
        </div>
      </div>
    </>
  );
}

function Ziel({ symbol: Symbol, titel, zusatz, onClick }) {
  return (
    <button className="zziel__knopf" onClick={onClick}>
      <span className="zziel__symbol">
        <Symbol groesse={18} />
      </span>
      <span>
        <span className="zziel__titel">{titel}</span>
        <span className="zziel__zusatz">{zusatz}</span>
      </span>
    </button>
  );
}

/*
  Das Pop-up an die Textstelle hängen — unterhalb, wenn dort Platz ist, sonst
  darüber. Auf einem Tablet ist der untere Bildschirmrand schnell erreicht;
  ein Kasten, der halb darunter verschwindet, wäre unbedienbar.
*/
function platziere(stelle) {
  if (!stelle) return { left: "50%", top: "20%", transform: "translateX(-50%)" };

  const fensterBreite = window.innerWidth;
  const fensterHoehe = window.innerHeight;

  let links = stelle.links;
  if (links + BREITE > fensterBreite - RAND) links = fensterBreite - BREITE - RAND;
  if (links < RAND) links = RAND;

  /* Grob geschätzte Höhe — genau genug für die Entscheidung oben oder unten. */
  const platzUnten = fensterHoehe - stelle.unten;
  const nachUnten = platzUnten > 340;

  return nachUnten
    ? { left: `${links}px`, top: `${stelle.unten + 8}px` }
    : { left: `${links}px`, bottom: `${fensterHoehe - stelle.oben + 8}px` };
}

function kuerze(text, laenge = 120) {
  return text.length <= laenge ? text : `${text.slice(0, laenge).trimEnd()} …`;
}
